/**
 * 图片 Agent - 现代化设计
 * 支持多模型 + 圈选区域处理
 */
import { AppSelect } from '../ui/AppSelect';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Image as ImageIcon, Upload, Sparkles, Loader2, AlertCircle,
  Wand2, Layers, Scissors, Trash2, Download, Palette,
  X, Plus, Maximize2, Undo2, Redo2, Brush, Eraser, ZoomIn, ZoomOut,
  FolderOpen, RefreshCw, RotateCcw, ChevronDown,
  Rocket, Brain, Box, Folder, Drama, Mountain, Wrench, Clapperboard
} from 'lucide-react';
import ReactDOM from 'react-dom';
import '../ui/GenerationSurface.css';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST } from '../../utils/constants';
import { extractTaskResultUrls, submitTask, startTaskPolling } from '../../services/taskService';
import { recycleStoredMedia } from '../../services/resourceRecycleService';
import ResourceSelector from '../resource/ResourceSelector';
import { IMAGE_MODEL_FAMILIES } from './imageModelFamilies';
import { ConfirmDialog, EmptyState } from '@ui';
import { uploadFileToCos } from '../../utils/fileUtils';
import { serializeEditor, stripRefMarkers, createMentionChip, renderPromptToEditor } from '../../utils/mentionUtils';
import { DEFAULT_IMAGE_QUALITY_TIER, IMAGE_QUALITY_TIER_OPTIONS, getStoredImageQualityTier, setStoredImageQualityTier } from '../../constants/imageQualityTiers';
import { getGlobalProviderRecommendation } from '../../constants/globalAiProviders';
import { DATASTART_IMAGE_MODELS, DATASTART_IMAGE_ASPECT_OPTIONS, DATASTART_IMAGE_MAX_REFERENCES, DATASTART_IMAGE_CAPABILITY_HINT, DATASTART_GENERATION_QUALITY_OPTIONS, DATASTART_EDIT_QUALITY_OPTIONS, isDatastartImageProvider, normalizeDatastartImageQuality } from '../../constants/datastartImageModels';
import {
  ASTRAFLOW_IMAGE_MODELS,
  ASTRAFLOW_IMAGE_QUALITY_OPTIONS,
  normalizeAstraflowImageQuality,
} from '../../constants/astraflowImageModels';
import { ASTRAFLOW_ASTRA_MODELS, normalizeAstraProviderId } from '../../constants/astraflowAstraModels';
import {
  DREAMFLY_IMAGE_MODELS,
  DREAMFLY_IMAGE_PROVIDER,
  DREAMFLY_IMAGE_QUALITY_TIERS,
  normalizeDreamflyImageModelId,
} from '../../constants/dreamflyModels';
import {
  IMAGE_PROVIDER_CHANGE_EVENT,
  IMAGE_PROVIDER_STORAGE_KEY,
  WANX_IMAGE_MAX_INPUT_IMAGES,
  assertWanxImageInputLimit,
  getCurrentImageProvider,
  getWanxCompatibleQualityOptions,
  normalizeWanxImageQualityTier,
  setStoredImageProvider,
} from '../../utils/wanxImageCapabilities';

const DREAMFLY_IMAGE_ASPECT_RATIO_OPTIONS = Object.freeze([
  { value: '1:1', label: '1:1 方形' },
  { value: '16:9', label: '16:9 宽屏' },
  { value: '9:16', label: '9:16 竖屏' },
  { value: '4:3', label: '4:3 标准' },
  { value: '3:4', label: '3:4 竖向' },
  { value: '21:9', label: '21:9 超宽屏' },
]);

const DREAMFLY_IMAGE_MODEL_CONFIGS = Object.fromEntries(
  DREAMFLY_IMAGE_MODELS.map(({ id, model, label }) => ([id, {
    id,
    name: label,
    provider: DREAMFLY_IMAGE_PROVIDER,
    imageModel: model,
    icon: Sparkles,
    color: '#06B6D4',
    modes: ['text2img', 'reference', 'region'],
    qualityTiers: DREAMFLY_IMAGE_QUALITY_TIERS,
    defaults: {
      aspectRatio: '1:1',
      quality: model === 'gpt-image-2' ? 'medium' : '',
    },
    aspectRatioOptions: DREAMFLY_IMAGE_ASPECT_RATIO_OPTIONS,
  }])),
);

// 模型配置（color 为模型身份/品牌色，跨主题保持一致，保留）
const IMAGE_MODEL_CONFIGS = {
  ...DREAMFLY_IMAGE_MODEL_CONFIGS,
  ...Object.fromEntries([...ASTRAFLOW_IMAGE_MODELS, ...ASTRAFLOW_ASTRA_MODELS].map(({ id, provider, model, label }) => [id, {
    id, provider, imageModel: model, name: label, icon: Sparkles, color: '#A78BFA',
    modes: ['text2img', 'reference', 'region'],
    qualityOptions: ASTRAFLOW_IMAGE_QUALITY_OPTIONS,
    defaults: { aspectRatio: '1:1', quality: 'high' },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  }])),
  ...Object.fromEntries(DATASTART_IMAGE_MODELS.map(({ id, provider, model, label }) => [id, {
    id, provider, imageModel: model, name: label, icon: Sparkles, color: '#38BDF8',
    modes: ['text2img', 'reference', 'region'], maxReferenceImages: DATASTART_IMAGE_MAX_REFERENCES,
    qualityTiers: ['1K'], qualityOptions: DATASTART_GENERATION_QUALITY_OPTIONS,
    defaults: { aspectRatio: '1:1', quality: 'auto' },
    aspectRatioOptions: DATASTART_IMAGE_ASPECT_OPTIONS,
  }])),
  newapi_image: {
    id: 'newapi_image',
    name: 'Image-2 · New API',
    provider: 'NEWAPI_IMAGE',
    imageModelByQuality: {
      '1K': 'gpt-image2-1k',
      '2K': 'gpt-image2-2k',
      '4K': 'gpt-image2-4k',
    },
    icon: Sparkles,
    color: '#EC4899',
    modes: ['text2img', 'reference', 'region'],
    maxReferenceImages: 3,
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
      { value: '21:9', label: '21:9 超宽屏' },
    ],
  },
  wangsugem: {
    id: 'wangsugem',
    name: 'NanoBanana2',
    provider: 'wangsugem',
    icon: Rocket,
    color: '#10B981',
    modes: ['text2img', 'reference', 'region'],
    defaults: {
      aspectRatio: '16:9',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '21:9', label: '21:9 超宽屏' },
      { value: '1:1', label: '1:1 方形' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  omini: {
    id: 'omini',
    name: 'Gemini_线路4',
    provider: 'OMINI',
    icon: Brain,
    color: '#FACC15',
    modes: ['text2img', 'reference', 'region'],
    defaults: {
      aspectRatio: '16:9',
      quality: 'standard',
    },
    aspectRatioOptions: [
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '21:9', label: '21:9 超宽屏' },
      { value: '1:1', label: '1:1 方形' },
      { value: '4:3', label: '4:3 标准' },
    ],
  },
  omini_openai: {
    id: 'omini_openai',
    name: 'Image-2_线路4',
    provider: 'OMINI_OPENAI',
    icon: Sparkles,
    color: '#FACC15',
    modes: ['text2img', 'reference', 'region'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
      { value: '21:9', label: '21:9 超宽屏' },
    ],
  },
  astraflow_gemini: {
    id: 'astraflow_gemini',
    name: 'Gemini_线路3',
    provider: 'ASTRAFLOW_GEMINI',
    icon: Sparkles,
    color: '#38BDF8',
    modes: ['text2img', 'reference', 'region'],
    defaults: {
      aspectRatio: '16:9',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '21:9', label: '21:9 超宽屏' },
      { value: '1:1', label: '1:1 方形' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  astraflow_openai: {
    id: 'astraflow_openai',
    name: 'Image-2_线路3 · GPT-5.5',
    provider: 'ASTRAFLOW_OPENAI',
    icon: Sparkles,
    color: '#A78BFA',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  astraflow_openai_luna: {
    id: 'astraflow_openai_luna',
    name: 'Image-2_线路3 · Luna',
    provider: 'ASTRAFLOW_OPENAI_LUNA',
    icon: Sparkles,
    color: '#A78BFA',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  astraflow_openai_terra: {
    id: 'astraflow_openai_terra',
    name: 'Image-2_线路3 · Terra',
    provider: 'ASTRAFLOW_OPENAI_TERRA',
    icon: Sparkles,
    color: '#A78BFA',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  astraflow_openai_sol: {
    id: 'astraflow_openai_sol',
    name: 'Image-2_线路3 · Sol',
    provider: 'ASTRAFLOW_OPENAI_SOL',
    icon: Sparkles,
    color: '#A78BFA',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  // 旧记录保留原型号说明；可见菜单与重新生成统一使用新的图文组合。
  astraflow_openai_astra: {
    id: 'astraflow_openai_astra',
    name: 'Image-2_线路3 · GPT-6 Astra',
    provider: 'ASTRAFLOW_OPENAI_ASTRA',
    imageModel: 'gpt-image-2',
    icon: Sparkles,
    color: '#A78BFA',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  astraflow_grok: {
    id: 'astraflow_grok',
    name: 'Grok Imagine_线路3',
    provider: 'ASTRAFLOW_GROK',
    icon: Sparkles,
    color: '#A78BFA',
    modes: ['text2img', 'reference', 'region'],
    defaults: {
      aspectRatio: '16:9',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '21:9', label: '21:9 超宽屏' },
      { value: '1:1', label: '1:1 方形' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
      { value: '3:2', label: '3:2 横向' },
      { value: '2:3', label: '2:3 竖向' },
    ],
  },
  wangsugpt: {
    id: 'wangsugpt',
    name: 'Image-2_线路1',
    provider: 'WANGSUGPT',
    icon: Sparkles,
    color: '#22D3EE',
    modes: ['text2img', 'reference', 'region'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
      { value: '21:9', label: '21:9 超宽屏' },
    ],
  },
  claude_opus_mj: {
    id: 'claude_opus_mj',
    name: 'Claude Opus + MJ',
    provider: 'CLAUDE_OPUS_MJ',
    icon: Sparkles,
    color: '#C084FC',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '16:9',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '21:9', label: '21:9 超宽屏' },
      { value: '1:1', label: '1:1 方形' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  gptimage: {
    id: 'gptimage',
    name: 'Image-2_线路2',
    provider: 'HUOBAO_GPT',
    icon: Palette,
    color: '#22C55E',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '1:1',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  huobao_gpt_image_cheap: {
    id: 'huobao_gpt_image_cheap',
    name: 'Image-2_线路2-4K',
    provider: 'HUOBAO_GPT_IMAGE_CHEAP',
    icon: Sparkles,
    color: '#FB923C',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '16:9',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '1:1', label: '1:1 方形' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  wanx: {
    id: 'wanx',
    name: '千问图像3.0 Pro',
    provider: 'WANX',
    imageModel: 'qwen-image-3.0-pro',
    icon: Drama,
    color: '#8B5CF6',
    modes: ['text2img', 'reference'],
    maxReferenceImages: WANX_IMAGE_MAX_INPUT_IMAGES,
    maxQualityTier: '2K',
    defaults: {
      aspectRatio: '16:9',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '1:1', label: '1:1 方形' },
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '21:9', label: '21:9 超宽屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:4', label: '3:4 竖向' },
    ],
  },
  // vidu: {
  //   id: 'vidu',
  //   name: 'Vidu',
  //   provider: 'deepseek',
  //   icon: Clapperboard,
  //   color: '#EC4899',
  //   modes: ['text2img', 'reference'],
  //   defaults: {
  //     aspectRatio: '16:9',
  //     quality: 'standard',
  //   },
  //   aspectRatioOptions: [
  //     { value: '16:9', label: '16:9 宽屏' },
  //     { value: '9:16', label: '9:16 竖屏' },
  //     { value: '21:9', label: '21:9 超宽屏' },
  //     { value: '1:1', label: '1:1 方形' },
  //   ],
  // },
  doubao: {
    id: 'doubao',
    name: '豆包',
    provider: 'DOUBAO',
    icon: Box,
    color: '#F97316',
    modes: ['text2img', 'reference'],
    defaults: {
      aspectRatio: '16:9',
      quality: 'high',
    },
    aspectRatioOptions: [
      { value: '16:9', label: '16:9 宽屏' },
      { value: '9:16', label: '9:16 竖屏' },
      { value: '21:9', label: '21:9 超宽屏' },
      { value: '4:3', label: '4:3 标准' },
      { value: '3:2', label: '3:2 横向' },
      { value: '1:1', label: '1:1 方形' },
    ],
  },
};

// 图片页历史上把火豹 GPT 图片线路命名为 gptimage；其余模型与全局 provider ID 一致。
const GLOBAL_PROVIDER_TO_IMAGE_MODEL = {
  huobao_gpt: 'gptimage',
};

const IMAGE_MODEL_TO_GLOBAL_PROVIDER = {
  gptimage: 'huobao_gpt',
};

const IMAGE_ONLY_MODEL_IDS = new Set([
  ...ASTRAFLOW_IMAGE_MODELS.map(({ id }) => id),
  ...DATASTART_IMAGE_MODELS.map(({ id }) => id),
  'newapi_image',
  'dreamfly_image',
  ...DREAMFLY_IMAGE_MODELS.map(({ id }) => id),
]);
const IMAGE_AGENT_MODEL_STORAGE_KEY = 'image_agent_model';

const resolveImageModelId = (providerId) => {
  const rawProviderId = normalizeAstraProviderId(providerId);
  if (!rawProviderId) return null;
  const dreamflyModelId = normalizeDreamflyImageModelId(rawProviderId);
  if (dreamflyModelId) return dreamflyModelId;
  if (IMAGE_MODEL_CONFIGS[rawProviderId]) return rawProviderId;
  const modelId = GLOBAL_PROVIDER_TO_IMAGE_MODEL[rawProviderId] || rawProviderId;
  return IMAGE_MODEL_CONFIGS[modelId] ? modelId : null;
};

const getInitialImageModelId = () => {
  const storedImageProvider = localStorage.getItem(IMAGE_PROVIDER_STORAGE_KEY);
  const resolvedImageProvider = resolveImageModelId(storedImageProvider);
  if (resolvedImageProvider) return resolvedImageProvider;
  const storedImageModelId = localStorage.getItem(IMAGE_AGENT_MODEL_STORAGE_KEY);
  const resolvedStoredModelId = resolveImageModelId(storedImageModelId);
  if (IMAGE_ONLY_MODEL_IDS.has(storedImageModelId) && resolvedStoredModelId) {
    return resolvedStoredModelId;
  }
  return resolveImageModelId(getCurrentImageProvider()) || 'wangsugem';
};

const normalizeGenerationMode = (modelConfig, requestedMode) => {
  const supportedModes = modelConfig?.modes || [];
  return supportedModes.includes(requestedMode)
    ? requestedMode
    : (supportedModes[0] || 'text2img');
};

const getImageQualityOptions = (modelConfig, generationMode) => (
  isDatastartImageProvider(modelConfig?.provider) && generationMode !== 'text2img'
    ? DATASTART_EDIT_QUALITY_OPTIONS : modelConfig?.qualityOptions || []
);

const normalizeImageQuality = (modelConfig, quality, generationMode = 'text2img') => {
  if (isDatastartImageProvider(modelConfig?.provider)) {
    return normalizeDatastartImageQuality(quality, generationMode !== 'text2img');
  }
  if (!modelConfig?.qualityOptions?.length) {
    return quality || modelConfig?.defaults?.quality;
  }
  return normalizeAstraflowImageQuality(quality);
};

// 老记录的引用可能是 URL、对象或只存在于提示词标记中；缺失不代表用户要清空草稿。
const getHistoryReferenceImages = (params = {}) => {
  const urls = (Array.isArray(params.referenceImages) ? params.referenceImages : [])
    .map(ref => typeof ref === 'string' ? ref : (ref?.url || ref?.imageUrl || ref?.base64))
    .filter(url => typeof url === 'string' && url.trim())
    .map(url => url.trim());
  const marked = params.markedPrompt || params.prompt || '';
  for (const match of marked.matchAll(/\{\{ref:(?:[^:}]*:)?[^:}]*:((?:https?:\/\/|data:image\/)[^}]+)\}\}/g)) {
    urls.push(match[1]);
  }
  return [...new Set(urls)];
};

const parseImageHistoryMetadata = (raw) => {
  const asRecord = (value) => {
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  };
  const metadata = asRecord(raw);
  const original = asRecord(metadata.originalMetadata);
  // 后端资源包装与早期 generationParams 嵌套格式都可恢复；较新的顶层 PATCH 优先。
  return { ...original, ...asRecord(original.generationParams), ...asRecord(metadata.generationParams), ...metadata };
};

const buildRegionMaskPixels = (strokePixels) => {
  const source = strokePixels instanceof Uint8ClampedArray
    ? strokePixels
    : new Uint8ClampedArray(strokePixels || []);
  const maskPixels = new Uint8ClampedArray(source.length);
  for (let i = 0; i < source.length; i += 4) {
    const value = source[i + 3] > 0 ? 255 : 0;
    maskPixels[i] = value;
    maskPixels[i + 1] = value;
    maskPixels[i + 2] = value;
    maskPixels[i + 3] = 255;
  }
  return maskPixels;
};

const restoreRegionStrokeSnapshot = (strokeCanvas, snapshot) => {
  if (!strokeCanvas || !snapshot) return;
  const ctx = strokeCanvas.getContext('2d');
  ctx.clearRect(0, 0, strokeCanvas.width, strokeCanvas.height);
  ctx.putImageData(snapshot, 0, 0);
};

const clearRegionStrokeCanvas = (strokeCanvas) => {
  if (!strokeCanvas) return;
  strokeCanvas.getContext('2d').clearRect(0, 0, strokeCanvas.width, strokeCanvas.height);
};

// 画布圈选组件
const RegionSelector = ({ imageUrl, onRegionSelected, onCancel }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const baseCanvasRef = useRef(null);
  const strokeCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [brushSize, setBrushSize] = useState(40);
  const [tool, setTool] = useState('brush');
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const [historyStatus, setHistoryStatus] = useState({ index: -1, length: 0 });
  const [scale, setScale] = useState(1);

  const saveToHistory = useCallback(() => {
    const strokeCanvas = strokeCanvasRef.current;
    if (!strokeCanvas) return;
    const ctx = strokeCanvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, strokeCanvas.width, strokeCanvas.height);

    const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
    newHistory.push(imageData);
    if (newHistory.length > 20) newHistory.shift();
    historyRef.current = newHistory;
    historyIndexRef.current = newHistory.length - 1;
    setHistoryStatus({ index: historyIndexRef.current, length: newHistory.length });
  }, []);

  // 初始化画布
  useEffect(() => {
    if (!imageUrl || !baseCanvasRef.current || !strokeCanvasRef.current) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const baseCanvas = baseCanvasRef.current;
      const strokeCanvas = strokeCanvasRef.current;
      if (!baseCanvas || !strokeCanvas) return;
      const baseCtx = baseCanvas.getContext('2d');

      baseCanvas.width = img.width;
      baseCanvas.height = img.height;
      strokeCanvas.width = img.width;
      strokeCanvas.height = img.height;

      baseCtx.clearRect(0, 0, baseCanvas.width, baseCanvas.height);
      baseCtx.drawImage(img, 0, 0);
      clearRegionStrokeCanvas(strokeCanvas);

      historyRef.current = [];
      historyIndexRef.current = -1;
      saveToHistory();
    };
    img.src = imageUrl;
  }, [imageUrl, saveToHistory]);

  const getCanvasCoordinates = (e) => {
    const canvas = strokeCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const undo = () => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      restoreRegionStrokeSnapshot(strokeCanvasRef.current, historyRef.current[historyIndexRef.current]);
      setHistoryStatus({ index: historyIndexRef.current, length: historyRef.current.length });
    }
  };

  const redo = () => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      restoreRegionStrokeSnapshot(strokeCanvasRef.current, historyRef.current[historyIndexRef.current]);
      setHistoryStatus({ index: historyIndexRef.current, length: historyRef.current.length });
    }
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = strokeCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = brushSize;
    
    if (tool === 'brush') {
      // 半透明红色标记（canvas 绘制色，与主题无关，保留）
      ctx.strokeStyle = 'rgba(255, 0, 0, 0.85)';
      ctx.globalCompositeOperation = 'source-over';
    } else {
      ctx.globalCompositeOperation = 'destination-out';
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = strokeCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoordinates(e);
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = strokeCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.closePath();
      ctx.globalCompositeOperation = 'source-over';
      saveToHistory();
    }
  };

  // 生成圈选蒙版
  const generateRegionMask = () => {
    const baseCanvas = baseCanvasRef.current;
    const strokeCanvas = strokeCanvasRef.current;
    if (!baseCanvas || !strokeCanvas) return;
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = strokeCanvas.width;
    maskCanvas.height = strokeCanvas.height;
    const maskCtx = maskCanvas.getContext('2d');
    
    // 黑色背景（未选中区域）
    maskCtx.fillStyle = 'black';
    maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
    
    // 只读取独立笔迹层的 Alpha；原图颜色不会参与圈选判断。
    const strokeData = strokeCanvas.getContext('2d')
      .getImageData(0, 0, strokeCanvas.width, strokeCanvas.height);
    const maskData = maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
    maskData.data.set(buildRegionMaskPixels(strokeData.data));
    maskCtx.putImageData(maskData, 0, 0);
    
    // 预览图临时合成原图与笔迹，原图画布本身不被修改。
    const markedCanvas = document.createElement('canvas');
    markedCanvas.width = baseCanvas.width;
    markedCanvas.height = baseCanvas.height;
    const markedCtx = markedCanvas.getContext('2d');
    markedCtx.drawImage(baseCanvas, 0, 0);
    markedCtx.drawImage(strokeCanvas, 0, 0);
    const markedImage = markedCanvas.toDataURL('image/png');
    const maskImage = maskCanvas.toDataURL('image/png');
    
    onRegionSelected({
      original: imageUrl, // 原始图片
      marked: markedImage, // 带标记的图片（用于展示）
      mask: maskImage, // 黑白蒙版（用于AI识别）
      width: baseCanvas.width,
      height: baseCanvas.height
    });
  };

  const clearCanvas = () => {
    clearRegionStrokeCanvas(strokeCanvasRef.current);
    saveToHistory();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm" style={{ backgroundColor: 'var(--overlay-heavy)' }}>
      <div className="flex flex-col w-full h-full max-w-7xl" style={{ backgroundColor: 'var(--color-bg-secondary)', borderRadius: '16px' }}>
        {/* 顶部工具栏 */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>圈选区域</h3>
            <span className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
              用红色画笔圈出想让AI关注的区域，提示词中描述如何处理该区域
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={undo} disabled={historyStatus.index <= 0} className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] disabled:opacity-30 transition-colors">
              <Undo2 size={18} />
            </button>
            <button onClick={redo} disabled={historyStatus.index >= historyStatus.length - 1} className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] disabled:opacity-30 transition-colors">
              <Redo2 size={18} />
            </button>
            <button onClick={clearCanvas} className="p-2 rounded-lg hover:bg-[var(--color-bg-tertiary)] text-[var(--color-error)] transition-colors">
              <Trash2 size={18} />
            </button>
            <div className="w-px h-6 mx-2" style={{ backgroundColor: 'var(--color-border-primary)' }} />
            <button onClick={onCancel} className="px-4 py-2 rounded-lg font-medium" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
              取消
            </button>
            <button 
              onClick={generateRegionMask} 
              className="px-4 py-2 rounded-lg font-medium text-[var(--color-accent-contrast)] flex items-center gap-2"
              style={{ backgroundColor: theme.colors.accentPrimary }}
            >
              确认圈选
            </button>
          </div>
        </div>

        {/* 工具栏 */}
        <div className="flex items-center gap-4 p-3 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div className="flex items-center gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
            <button
              onClick={() => setTool('brush')}
              className={`p-2 rounded flex items-center gap-1.5 text-sm`}
              style={{ 
                backgroundColor: tool === 'brush' ? theme.colors.accentPrimary : 'transparent', 
                color: tool === 'brush' ? 'white' : 'var(--color-text-secondary)' 
              }}
            >
              <Brush size={16} />
              圈选
            </button>
            <button
              onClick={() => setTool('eraser')}
              className={`p-2 rounded flex items-center gap-1.5 text-sm`}
              style={{ 
                backgroundColor: tool === 'eraser' ? theme.colors.accentPrimary : 'transparent', 
                color: tool === 'eraser' ? 'white' : 'var(--color-text-secondary)' 
              }}
            >
              <Eraser size={16} />
              擦除
            </button>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>笔刷大小</span>
            <input
              type="range"
              min="10"
              max="150"
              value={brushSize}
              onChange={(e) => setBrushSize(parseInt(e.target.value))}
              className="w-28"
            />
            <span className="text-xs w-10" style={{ color: 'var(--color-text-muted)' }}>{brushSize}px</span>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => setScale(s => Math.max(0.3, s - 0.1))} className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)]">
              <ZoomOut size={16} />
            </button>
            <span className="text-xs w-14 text-center" style={{ color: 'var(--color-text-muted)' }}>{Math.round(scale * 100)}%</span>
            <button onClick={() => setScale(s => Math.min(3, s + 0.1))} className="p-1.5 rounded hover:bg-[var(--color-bg-tertiary)]">
              <ZoomIn size={16} />
            </button>
          </div>
        </div>

        {/* 画布区域 */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center">
          <div style={{ transform: `scale(${scale})`, transformOrigin: 'center', transition: 'transform 0.2s' }}>
            <div className="relative shadow-2xl rounded overflow-hidden" style={{ lineHeight: 0 }}>
              <canvas
                ref={baseCanvasRef}
                aria-hidden="true"
                className="block pointer-events-none"
                style={{ maxWidth: 'none', maxHeight: 'none' }}
              />
              <canvas
                ref={strokeCanvasRef}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                className="absolute inset-0 w-full h-full cursor-crosshair"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Module-level helper (outside component)
const formatTime = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// 图片卡片组件
const ImageCard = React.memo(({ image, onDelete, onRetry, onUpscale, onHoverPreview }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [showPreview, setShowPreview] = useState(false);

  const handleDownload = async () => {
    if (!image.imageUrl) return;
    try {
      const response = await fetch(image.imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `image_${image.id}.png`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('下载失败:', e);
    }
  };

  return (
    <>
      <div className="ia-card generation-result group relative overflow-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-secondary)' }}>
        <div className="generation-result-media relative aspect-video overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
          {image.status === 'processing' ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
              <Loader2 size={40} className="animate-spin" style={{ color: theme.colors.accentPrimary }} />
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>生成中...</span>
            </div>
          ) : image.status === 'failed' ? (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 p-4" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
              <AlertCircle size={36} style={{ color: 'var(--color-error)' }} />
              <span className="text-xs text-center line-clamp-2" style={{ color: 'var(--color-error)' }}>{image.errorMessage || '生成失败'}</span>
              <button
                onClick={() => onRetry?.(image)}
                className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5"
                style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary }}
              >
                <RotateCcw size={12} /> 重试
              </button>
            </div>
          ) : image.imageUrl ? (
            <img src={image.imageUrl} alt={image.prompt} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
              <Loader2 size={40} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />
            </div>
          )}

          {image.imageUrl && image.status !== 'processing' && image.status !== 'failed' && (
            <div className="generation-result-overlay absolute inset-0 flex items-center justify-center gap-2">
              <button onClick={() => setShowPreview(true)} className="generation-media-action p-2 rounded-md text-white" aria-label="放大预览" title="放大预览">
                <Maximize2 size={18} />
              </button>
              <button onClick={handleDownload} className="generation-media-action p-2 rounded-md text-white" aria-label="下载图片" title="下载图片">
                <Download size={18} />
              </button>
              <button onClick={() => onRetry?.(image)} className="generation-media-action p-2 rounded-md text-white" aria-label="使用相同参数重新生成" title="使用相同参数重新生成">
                <RefreshCw size={18} />
              </button>
              <button onClick={() => onUpscale?.(image)} className="generation-media-action p-2 rounded-md text-white" aria-label="高清化" title="高清化">
                <ZoomIn size={18} />
              </button>
              <button onClick={() => onDelete?.(image)} className="generation-media-action generation-media-action--danger p-2 rounded-md text-white" style={{ backgroundColor: 'var(--color-error)' }} aria-label="删除图片" title="删除图片">
                <Trash2 size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="generation-result-body p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium line-clamp-2 leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>{image.prompt || '无提示词'}</p>
              <div className="generation-result-meta flex items-center gap-3 mt-2">
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{formatTime(image.createdAt)}</span>
                {image.aspectRatio && (
                  <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
                    {image.aspectRatio}
                  </span>
                )}
              </div>
            </div>
            <span className="text-2xs px-2 py-1 rounded-md whitespace-nowrap" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}>
              {image.model}
            </span>
          </div>

          {image.referenceImages?.length > 0 && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>参考:</span>
              <div className="flex gap-1">
                {image.referenceImages.slice(0, 3).map((ref, i) => (
                  <img key={i} src={ref} alt="" className="w-8 h-8 rounded object-cover cursor-pointer"
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); onHoverPreview?.({ url: ref, x: r.left + r.width / 2, y: r.top }); }}
                    onMouseLeave={() => onHoverPreview?.(null)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-8 animate-in fade-in duration-200" style={{ backgroundColor: 'var(--overlay-heavy)' }} onClick={() => setShowPreview(false)}>
          <img src={image.imageUrl} alt="" className="max-w-full max-h-full rounded-lg animate-in fade-in zoom-in-95 duration-200" />
        </div>
      )}
    </>
  );
});

// 主组件
const ImageAgentModern = ({ projectId, episodeId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  
  const [selectedModel, setSelectedModel] = useState(getInitialImageModelId);
  const [generationMode, setGenerationMode] = useState(() => (
    IMAGE_MODEL_CONFIGS[getInitialImageModelId()]?.modes?.[0] || 'text2img'
  ));
  const [config, setConfig] = useState(() => ({
    ...(IMAGE_MODEL_CONFIGS[getInitialImageModelId()]?.defaults || IMAGE_MODEL_CONFIGS.wangsugem.defaults),
  }));
  const [prompt, setPrompt] = useState('');
  const [images, setImages] = useState({ refs: [], source: null });
  const [regionData, setRegionData] = useState(null); // { original, marked, mask }
  const [imageList, setImageList] = useState([]);
  const [error, setError] = useState(null);
  const [showRegionEditor, setShowRegionEditor] = useState(false);
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [mentionPopover, setMentionPopover] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [hoverPreview, setHoverPreview] = useState(null);
  const [mentionPreviewUrl, setMentionPreviewUrl] = useState(null);

  const editorRef = useRef(null);
  const addMenuRef = useRef(null);
  const imageInputRef = useRef(null);
  const mentionUploadRef = useRef(null);
  const mentionListRef = useRef(null);
  const mentionItemRefs = useRef([]);
  const mentionIndexRef = useRef(0);
  const mentionAtNodeRef = useRef(null);
  const mentionAtIndexRef = useRef(-1);
  const historyRequestSeqRef = useRef(0);

  const modelConfig = IMAGE_MODEL_CONFIGS[selectedModel] || IMAGE_MODEL_CONFIGS.wangsugem;
  const selectedModelRef = useRef(selectedModel);
  const [imageQualityTier, setImageQualityTier] = useState(getStoredImageQualityTier);
  const maxReferenceImages = modelConfig.maxReferenceImages ?? Number.POSITIVE_INFINITY;
  const providerCompatibleQualityTierOptions = getWanxCompatibleQualityOptions(
    IMAGE_QUALITY_TIER_OPTIONS,
    modelConfig.provider,
  );
  const availableQualityTierOptions = modelConfig.qualityTiers
    ? providerCompatibleQualityTierOptions.filter((option) => modelConfig.qualityTiers.includes(option.value))
    : providerCompatibleQualityTierOptions;

  const selectImageModel = useCallback((modelId, persistSelection = true) => {
    const nextConfig = IMAGE_MODEL_CONFIGS[modelId];
    if (!nextConfig || selectedModelRef.current === modelId) return;

    selectedModelRef.current = modelId;
    setSelectedModel(modelId);
    setConfig(prev => ({
      ...nextConfig.defaults,
      aspectRatio: nextConfig.aspectRatioOptions?.some(option => option.value === prev.aspectRatio)
        ? prev.aspectRatio
        : nextConfig.defaults.aspectRatio,
    }));
    setGenerationMode(prev => normalizeGenerationMode(nextConfig, prev));
    // 模式不兼容时只切换模式；参考图、圈选原图/蒙版和提示词继续暂存。
    setShowRegionEditor(false);
    if (IMAGE_ONLY_MODEL_IDS.has(modelId)) {
      localStorage.setItem(IMAGE_AGENT_MODEL_STORAGE_KEY, modelId);
    } else {
      localStorage.removeItem(IMAGE_AGENT_MODEL_STORAGE_KEY);
    }
    if (persistSelection) {
      const imageProvider = IMAGE_MODEL_TO_GLOBAL_PROVIDER[modelId] || modelId;
      setStoredImageProvider(imageProvider);
    }
  }, []);

  useEffect(() => {
    const syncFromImageProvider = (event) => {
      if (event.type === 'storage' && event.key !== IMAGE_PROVIDER_STORAGE_KEY
        && !(event.key === 'ai_provider' && !localStorage.getItem(IMAGE_PROVIDER_STORAGE_KEY))) return;
      const providerId = event?.detail?.provider || getCurrentImageProvider();
      const modelId = resolveImageModelId(providerId);
      if (modelId) selectImageModel(modelId, false);
    };

    window.addEventListener('storage', syncFromImageProvider);
    window.addEventListener(IMAGE_PROVIDER_CHANGE_EVENT, syncFromImageProvider);
    return () => {
      window.removeEventListener('storage', syncFromImageProvider);
      window.removeEventListener(IMAGE_PROVIDER_CHANGE_EVENT, syncFromImageProvider);
    };
  }, [selectImageModel]);

  useEffect(() => {
    const normalizedQualityTier = normalizeWanxImageQualityTier(
      imageQualityTier,
      modelConfig.provider,
    );
    if (normalizedQualityTier !== imageQualityTier) {
      setImageQualityTier(setStoredImageQualityTier(normalizedQualityTier));
    }
  }, [imageQualityTier, modelConfig.provider]);

  // 加载历史记录（从 agent_resource 旧表 + unified_resource 新表）
  useEffect(() => {
    if (projectId) {
      loadHistory();
    }
    return () => {
      historyRequestSeqRef.current += 1;
    };
  }, [projectId, episodeId]);

  const loadHistory = async () => {
    const requestSeq = ++historyRequestSeqRef.current;
    try {
      const allImages = [];

      // 1. 从 unified_resource 表读取新数据
      const unifiedPayload = {
        projectId: parseInt(projectId),
        types: ['AGENT_IMAGE'],
      };
      if (episodeId) {
        unifiedPayload.episodeId = parseInt(episodeId);
      }

      const unifiedResources = await authenticatedFetch(`${API_HOST}/api/resource/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(unifiedPayload)
      });

      if (Array.isArray(unifiedResources)) {
        const unifiedImages = unifiedResources.map(res => {
          const metadata = parseImageHistoryMetadata(res.metadata);

          const storedModelId = String(metadata.modelId || metadata.provider || metadata.model || 'omini').trim().toLowerCase();
          const modelId = IMAGE_MODEL_CONFIGS[storedModelId]
            ? storedModelId
            : (resolveImageModelId(storedModelId) || storedModelId);
          return {
            id: res.id,
            resourceContext: { projectId, episodeId },
            status: 'success',
            imageUrl: res.url,
            prompt: res.prompt,
            model: IMAGE_MODEL_CONFIGS[modelId]?.name || modelId,
            aspectRatio: metadata.aspectRatio || '16:9',
            createdAt: res.createdAt,
            generationParams: {
              ...metadata,
              prompt: metadata.prompt || res.prompt || '',
              modelId
            }
          };
        });
        allImages.push(...unifiedImages);
      }

      // 2. 从 agent_resource 旧表读取旧数据
      const agentResources = await authenticatedFetch(`${API_HOST}/api/agent-resource/images?projectId=${projectId}${episodeId ? `&episodeId=${episodeId}` : ''}`);

      if (Array.isArray(agentResources)) {
        const agentImages = agentResources.map(res => {
          const metadata = parseImageHistoryMetadata(res.metadata);

          const storedModelId = String(metadata.modelId || metadata.provider || metadata.model || 'omini').trim().toLowerCase();
          const modelId = IMAGE_MODEL_CONFIGS[storedModelId]
            ? storedModelId
            : (resolveImageModelId(storedModelId) || storedModelId);
          return {
            id: 'agent_' + res.id,
            resourceContext: { projectId, episodeId },
            status: 'success',
            imageUrl: res.url,
            prompt: res.prompt,
            model: IMAGE_MODEL_CONFIGS[modelId]?.name || modelId,
            aspectRatio: metadata.aspectRatio || '16:9',
            createdAt: res.createdAt,
            generationParams: {
              ...metadata,
              modelId
            }
          };
        });
        allImages.push(...agentImages);
      }

      // 3. 按时间倒序排序
      allImages.sort((a, b) => {
        const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return timeB - timeA;
      });

      if (requestSeq !== historyRequestSeqRef.current) return;
      setImageList((currentImages) => {
        const loadedKeys = new Set(allImages.map((image) => (
          image?.imageUrl ? `url:${image.imageUrl}` : `id:${image?.id}`
        )));
        const localOnly = currentImages.filter((image) => {
          const key = image?.imageUrl ? `url:${image.imageUrl}` : `id:${image?.id}`;
          return !loadedKeys.has(key);
        });
        return [...localOnly, ...allImages].sort((a, b) => {
          const timeA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const timeB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return timeB - timeA;
        });
      });
    } catch (e) {
      if (requestSeq === historyRequestSeqRef.current) {
        console.error("加载历史记录失败:", e);
      }
    }
  };

  const handleImageQualityTierChange = (value) => {
    setImageQualityTier(setStoredImageQualityTier(value));
  };

  // ❌ 移除 useEffect，改为在点击模型时手动重置配置，避免覆盖 handleRetry 的设置
  // useEffect(() => {
  //   if (modelConfig) {
  //     setConfig(modelConfig.defaults);
  //   }
  // }, [selectedModel]);

  const updateConfig = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  const handleImageUpload = (type, file) => {
    if (!file) return;
    if (type === 'refs' && images.refs.length >= maxReferenceImages) {
      toast.warning(`${modelConfig.name}最多支持${maxReferenceImages}张参考图`);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      if (type === 'refs') {
        setImages(prev => {
          const newRefs = [...prev.refs, { id: Date.now(), url: e.target.result }];
          appendChipToEditor(`图片${newRefs.length}`, e.target.result, 'upload');
          return { ...prev, refs: newRefs };
        });
      } else {
        setImages(prev => ({ ...prev, [type]: e.target.result }));
        setRegionData(null);
        if (generationMode === 'region') {
          setShowRegionEditor(true);
        }
      }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (type, id) => {
    if (type === 'refs') {
      setImages(prev => ({ ...prev, refs: prev.refs.filter(r => r.id !== id) }));
    } else {
      setImages(prev => ({ ...prev, [type]: null }));
      if (type === 'source') setRegionData(null);
    }
  };

  // 重新生成 - 回填生成参数
  const handleRetry = (image) => {
    if (!image.generationParams) {
      toast.warning('该图片的生成参数已丢失，无法重新生成');
      return;
    }
    const {
      modelId,
      imageModel,
      generationMode: mode,
      aspectRatio,
      imageQualityTier: savedQualityTier,
      quality: savedQuality,
      prompt: savedPrompt,
      markedPrompt: savedMarked,
      regionOriginal,
      regionMask,
    } = image.generationParams;
    const normalizedModelId = String(modelId || '').trim().toLowerCase();
    const retryModelId = resolveImageModelId(normalizedModelId)
      || resolveImageModelId(imageModel)
      || (normalizedModelId.startsWith('qwen-image-3.0') ? 'wanx' : selectedModel);
    const retryModelConfig = IMAGE_MODEL_CONFIGS[retryModelId] || IMAGE_MODEL_CONFIGS.wangsugem;
    const retryReferenceImages = getHistoryReferenceImages(image.generationParams);
    const requestedMode = mode || (regionOriginal ? 'region' : retryReferenceImages.length ? 'reference' : generationMode);
    const retryMode = normalizeGenerationMode(retryModelConfig, requestedMode);

    selectedModelRef.current = retryModelId;
    setSelectedModel(retryModelId);
    setGenerationMode(retryMode);
    setShowRegionEditor(false);
    const defaults = retryModelConfig.defaults || {};
    setConfig({
      ...defaults,
      aspectRatio: aspectRatio || defaults.aspectRatio,
      quality: normalizeImageQuality(retryModelConfig, savedQuality, retryMode),
    });
    if (savedQualityTier) {
      const normalizedQualityTier = normalizeWanxImageQualityTier(
        savedQualityTier,
        retryModelConfig.provider,
      );
      setImageQualityTier(setStoredImageQualityTier(normalizedQualityTier));
    }

    // 恢复提示词（优先用带 @ 标记的 markedPrompt）
    const promptToRestore = savedMarked || savedPrompt || '';
    setPrompt(promptToRestore);
    renderPromptToEditor(editorRef.current, promptToRestore, retryModelConfig.color || '#3B82F6');

    // 恢复图片数据
    if (regionOriginal) {
      // 圈选模式：恢复源图和蒙版
      setImages(prev => ({
        ...prev,
        source: regionOriginal
      }));
      setRegionData(regionMask ? {
        original: regionOriginal,
        marked: regionOriginal,  // 用原图显示，等待重新圈选
        mask: regionMask
      } : null);
    }
    if (retryReferenceImages.length > 0) {
      // 参考生图模式
      setImages(prev => ({
        ...prev,
        refs: retryReferenceImages.map(url => ({ id: url, url })),
      }));
    }

    if (retryMode !== requestedMode) {
      toast.warning('原生成模式已不受当前模型支持，已切换到可用模式');
    } else if (retryMode === 'reference' && retryReferenceImages.length > (retryModelConfig.maxReferenceImages ?? Number.POSITIVE_INFINITY)) {
      toast.warning(`${retryModelConfig.name}最多支持${retryModelConfig.maxReferenceImages}张参考图，请删除多余图片后再生成`);
    } else {
      toast.info('已恢复生成参数，请点击生成按钮重新生成');
    }
  };

  const handleRegionSelected = (data) => {
    setRegionData(data);
    setShowRegionEditor(false);
    toast.success('区域已圈选，请描述如何处理该区域');
  };

  // ── @ 提及系统 ──
  const updateMentionIndex = useCallback((newIndex) => {
    mentionIndexRef.current = newIndex;
    mentionItemRefs.current.forEach((el, i) => {
      if (!el) return;
      if (i === newIndex) {
        el.style.backgroundColor = `${modelConfig?.color || '#3B82F6'}18`;
        el.style.color = modelConfig?.color || '#3B82F6';
        el.scrollIntoView?.({ block: 'nearest' });
        const imgUrl = el.dataset.imageUrl;
        if (imgUrl) setMentionPreviewUrl(imgUrl);
        else setMentionPreviewUrl(null);
      } else {
        el.style.backgroundColor = 'transparent';
        el.style.color = 'var(--color-text-secondary)';
      }
    });
  }, [modelConfig]);

  const getPromptWithMarkers = useCallback(() => serializeEditor(editorRef.current), []);

  const mentionSource = useMemo(() => {
    return images.refs.map((r, i) => ({ id: r.id, name: `图片${i + 1}`, url: r.url }));
  }, [images.refs]);

  // 参考图删除时同步 editor 中的 chips
  useEffect(() => {
    if (!editorRef.current) return;
    const chips = editorRef.current.querySelectorAll('[data-mention]');
    if (chips.length === 0) return;
    const urlSet = new Set(mentionSource.map(m => m.url));
    let changed = false;
    chips.forEach(chip => {
      if (!urlSet.has(chip.dataset.url)) { chip.remove(); changed = true; }
    });
    if (changed) setPrompt(getPromptWithMarkers());
  }, [mentionSource, getPromptWithMarkers]);

  const filteredMentions = useMemo(() => {
    if (!mentionPopover) return [];
    const q = mentionPopover.searchText || '';
    if (mentionPopover.level === 'root') {
      const rootItems = [
        { id: 'resource', icon: Folder, label: '资源管理器', action: 'navigate', nextLevel: 'source' },
        { id: 'upload', icon: Upload, label: '本地上传', action: 'upload' },
      ];
      return q ? rootItems.filter(i => i.label.toLowerCase().includes(q)) : rootItems;
    }
    if (['source', 'assets', 'variants'].includes(mentionPopover.level)) {
      return q ? menuItems.filter(i => i.label.toLowerCase().includes(q)) : menuItems;
    }
    return mentionSource.filter(m => !q || m.name.toLowerCase().includes(q));
  }, [mentionPopover, mentionSource, menuItems]);

  useEffect(() => { if (!mentionPopover) setMentionPreviewUrl(null); }, [mentionPopover]);

  const handleEditorInput = useCallback(() => {
    setPrompt(getPromptWithMarkers());
    // 参考附件独立于提示词：历史记录或纯文本提示词不一定含每张图的 mention。
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { setMentionPopover(null); return; }
    const range = sel.getRangeAt(0);
    let cursorNode = range.startContainer;
    let cursorOffset = range.startOffset;
    if (cursorNode.nodeType !== Node.TEXT_NODE) { setMentionPopover(null); return; }
    const textBefore = cursorNode.textContent.substring(0, cursorOffset);
    const lastAt = textBefore.lastIndexOf('@');
    if (lastAt !== -1) {
      const afterAt = textBefore.substring(lastAt + 1);
      if (!afterAt.includes(' ') && !afterAt.includes('\n')) {
        const rect = editorRef.current.getBoundingClientRect();
        setMentionPopover({ x: rect.left + 10, y: rect.top, searchText: afterAt.toLowerCase(), level: 'root', path: [] });
        mentionAtNodeRef.current = cursorNode;
        mentionAtIndexRef.current = lastAt;
        mentionIndexRef.current = 0;
        setMenuItems([]);
        return;
      }
    }
    setMentionPopover(null);
  }, [getPromptWithMarkers]);

  const handleMentionSelect = useCallback((item) => {
    if (!mentionPopover || !editorRef.current) return;
    const atNode = mentionAtNodeRef.current;
    const atIdx = mentionAtIndexRef.current;
    if (!atNode || atIdx < 0 || !atNode.parentNode) { setMentionPopover(null); return; }
    const searchLen = (mentionPopover.searchText || '').length;
    const endOffset = Math.min(atIdx + 1 + searchLen, (atNode.textContent || '').length);
    const deleteRange = document.createRange();
    deleteRange.setStart(atNode, atIdx);
    deleteRange.setEnd(atNode, endOffset);
    deleteRange.deleteContents();
    const chip = createMentionChip(item.name, item.url, modelConfig?.color || '#3B82F6', item.category || '');
    const afterNode = atNode.textContent.length > atIdx ? atNode.splitText(atIdx) : null;
    if (afterNode) atNode.parentNode.insertBefore(chip, afterNode);
    else if (atNode.parentNode) atNode.parentNode.insertBefore(chip, atNode.nextSibling);
    else editorRef.current.appendChild(chip);
    const space = document.createTextNode('\u00A0');
    chip.after(space);
    const sel = window.getSelection();
    const newRange = document.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    mentionAtNodeRef.current = null;
    mentionAtIndexRef.current = -1;
    setMentionPopover(null);
    setPrompt(getPromptWithMarkers());
  }, [mentionPopover, modelConfig, getPromptWithMarkers]);

  const handleMenuNavigate = useCallback(async (item) => {
    if (!mentionPopover) return;
    const prev = mentionPopover;
    if (item.action === 'upload') { mentionUploadRef.current?.click(); return; }
    if (item.action === 'navigate') {
      const nextLevel = item.nextLevel;
      const nextPath = [...prev.path, item.label];
      setMentionPopover({ ...prev, level: nextLevel, path: nextPath, category: item.category, asset: item.asset, searchText: '' });
      mentionIndexRef.current = 0;
      setMenuLoading(true);
      try {
        let items = [];
        if (nextLevel === 'source') {
          items = [
            { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
            { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
            { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
            { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
          ];
        } else if (nextLevel === 'assets') {
          const cat = item.category;
          if (cat === 'character') {
            const data = await authenticatedFetch(`${API_HOST}/api/characters?projectId=${projectId}`);
            items = (data || []).filter(c => c.imageUrl).map(c => ({
              id: c.id, icon: null, label: c.name, imageUrl: c.imageUrl, url: c.imageUrl,
              action: (c.variations?.length > 0) ? 'navigate' : 'select', nextLevel: 'variants', category: cat, asset: c,
            }));
          } else if (cat === 'scene') {
            const data = await authenticatedFetch(`${API_HOST}/api/scenes?projectId=${projectId}`);
            items = (data || []).filter(s => s.imageUrl).map(s => ({
              id: s.id, icon: null, label: s.name, imageUrl: s.imageUrl, url: s.imageUrl,
              action: (s.variations?.length > 0) ? 'navigate' : 'select', nextLevel: 'variants', category: cat, asset: s,
            }));
          } else if (cat === 'prop') {
            const data = await authenticatedFetch(`${API_HOST}/api/props?projectId=${projectId}`);
            items = (data || []).filter(p => p.imageUrl).map(p => ({
              id: p.id, icon: null, label: p.name, imageUrl: p.imageUrl, url: p.imageUrl,
              action: (p.variations?.length > 0) ? 'navigate' : 'select', nextLevel: 'variants', category: cat, asset: p,
            }));
          } else if (cat === 'storyboard') {
            const data = await authenticatedFetch(`${API_HOST}/api/storyboard/load?episodeId=${episodeId}`);
            const shots = (data || []).filter(s => s.generated_image);
            items = shots.map((s, i) => ({
              id: `shot-${i}`, icon: null, label: `镜头${s.shot_number || i + 1}`, imageUrl: s.generated_image, url: s.generated_image, action: 'select',
            }));
          }
        } else if (nextLevel === 'variants') {
          const asset = item.asset;
          const variantCategory = item.category || prev.category || '';
          if (asset) {
            items = [{ id: 'main', icon: null, label: asset.name || '主体', imageUrl: asset.imageUrl, url: asset.imageUrl, action: 'select', category: variantCategory }];
            (asset.variations || []).forEach((v, i) => {
              if (v.imageUrl) items.push({ id: `var-${i}`, icon: null, label: asset.name || '主体', imageUrl: v.imageUrl, url: v.imageUrl, action: 'select', category: variantCategory });
            });
          }
        }
        setMenuItems(items);
      } catch (err) { console.error('加载菜单失败:', err); setMenuItems([]); }
      finally { setMenuLoading(false); }
    }
  }, [mentionPopover, projectId, episodeId]);

  const handleMenuSelect = useCallback((item) => {
    if (!item.url) return;
    const alreadyAdded = images.refs.some(r => r.url === item.url);
    if (!alreadyAdded && images.refs.length >= maxReferenceImages) {
      toast.warning(`${modelConfig.name}最多支持${maxReferenceImages}张参考图`);
      return;
    }
    if (!alreadyAdded) {
      setImages(prev => ({ ...prev, refs: [...prev.refs, { id: Date.now(), url: item.url }] }));
    }
    handleMentionSelect({ id: item.id, name: item.label, url: item.url, category: mentionPopover?.category || item.category || '' });
  }, [images.refs, handleMentionSelect, maxReferenceImages, mentionPopover, modelConfig.name, toast]);

  const handleMentionUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadFileToCos(file);
      const alreadyAdded = images.refs.some(r => r.url === url);
      if (!alreadyAdded && images.refs.length >= maxReferenceImages) {
        toast.warning(`${modelConfig.name}最多支持${maxReferenceImages}张参考图`);
        return;
      }
      if (!alreadyAdded) {
        setImages(prev => ({ ...prev, refs: [...prev.refs, { id: Date.now(), url }] }));
      }
      handleMentionSelect({ id: Date.now(), name: file.name.split('.')[0] || '本地图片', url, category: 'upload' });
    } catch (err) { toast.error('上传失败: ' + err.message); }
    finally { e.target.value = ''; }
  }, [images.refs, handleMentionSelect, maxReferenceImages, modelConfig.name, toast]);

  const handleMenuBack = useCallback(() => {
    if (!mentionPopover || mentionPopover.path.length === 0) return;
    const newPath = [...mentionPopover.path];
    newPath.pop();
    if (newPath.length === 0) {
      setMentionPopover({ ...mentionPopover, level: 'root', path: [], searchText: '' });
      setMenuItems([]);
    } else {
      setMentionPopover({ ...mentionPopover, level: 'source', path: ['资源管理器'], searchText: '' });
      setMenuItems([
        { id: 'character', icon: Drama, label: '角色工坊', action: 'navigate', nextLevel: 'assets', category: 'character' },
        { id: 'scene', icon: Mountain, label: '场景工坊', action: 'navigate', nextLevel: 'assets', category: 'scene' },
        { id: 'prop', icon: Wrench, label: '道具工坊', action: 'navigate', nextLevel: 'assets', category: 'prop' },
        { id: 'storyboard', icon: Clapperboard, label: '分镜', action: 'navigate', nextLevel: 'assets', category: 'storyboard' },
      ]);
    }
    mentionIndexRef.current = 0;
  }, [mentionPopover]);

  // 往 editor 末尾追加 chip（非 @ 方式添加参考图时调用）
  const appendChipToEditor = useCallback((name, url, category = '') => {
    const el = editorRef.current;
    if (!el) return;
    const chip = createMentionChip(name, url, modelConfig?.color || '#3B82F6', category);
    const lastChild = el.lastChild;
    if (!lastChild || (lastChild.nodeType === Node.TEXT_NODE && !lastChild.textContent.endsWith(' ')) || lastChild.nodeType !== Node.TEXT_NODE) {
      el.appendChild(document.createTextNode(' '));
    }
    el.appendChild(chip);
    el.appendChild(document.createTextNode('\u00A0'));
    setPrompt(getPromptWithMarkers());
  }, [modelConfig, getPromptWithMarkers]);

  const handleEditorKeyDown = useCallback((e) => {
    if (mentionPopover && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); updateMentionIndex((mentionIndexRef.current + 1) % filteredMentions.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); updateMentionIndex((mentionIndexRef.current - 1 + filteredMentions.length) % filteredMentions.length); return; }
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        const item = filteredMentions[mentionIndexRef.current];
        if (item?.action === 'select') handleMenuSelect(item);
        else if (item?.action === 'navigate' || item?.action === 'upload') handleMenuNavigate(item);
        else handleMentionSelect(item);
        return;
      }
      if (e.key === 'ArrowLeft') { e.preventDefault(); if (mentionPopover.path?.length > 0) handleMenuBack(); else setMentionPopover(null); return; }
      if (e.key === 'Escape') { e.preventDefault(); setMentionPopover(null); return; }
    }
    if (e.key === 'Backspace') {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && sel.isCollapsed) {
        const range = sel.getRangeAt(0);
        const node = range.startContainer;
        const syncAfterDelete = () => {
          setPrompt(getPromptWithMarkers());
        };
        if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
          const prev = node.previousSibling;
          if (prev?.dataset?.mention) { e.preventDefault(); prev.remove(); syncAfterDelete(); return; }
        }
        if (node === editorRef.current) {
          const child = node.childNodes[range.startOffset - 1];
          if (child?.dataset?.mention) { e.preventDefault(); child.remove(); syncAfterDelete(); return; }
        }
      }
    }
  }, [mentionPopover, filteredMentions, updateMentionIndex, handleMentionSelect, handleMenuSelect, handleMenuNavigate, handleMenuBack, getPromptWithMarkers]);

  // Stable onDelete callback for ImageCard
  const [deleteTarget, setDeleteTarget] = useState(null);
  const deleteScopeRef = useRef('');
  deleteScopeRef.current = JSON.stringify([projectId, episodeId]);
  const handleDeleteImage = useCallback((img) => {
    setDeleteTarget(img);
  }, []);
  const confirmDeleteImage = useCallback(async () => {
    if (!deleteTarget) return;
    const scope = deleteScopeRef.current;
    const stored = deleteTarget.status === 'success';
    try {
      if (stored) await recycleStoredMedia({ ...deleteTarget.resourceContext, url: deleteTarget.imageUrl });
      if (scope !== deleteScopeRef.current) return;
      historyRequestSeqRef.current += 1;
      setImageList(prev => prev.filter(image => image.id !== deleteTarget.id && !(stored && image.imageUrl === deleteTarget.imageUrl)));
      setDeleteTarget(null);
      toast.success(stored ? '图片已移入回收站，可联系管理员恢复。' : '已移除本地任务记录，未取消生成任务。');
    } catch (error) {
      toast.error(error?.message || '移入回收站失败，图片记录已保留。');
      throw error;
    }
  }, [deleteTarget, toast]);

  // 高清化
  const handleUpscale = useCallback(async (img) => {
    if (!img.imageUrl) return;
    const upscaleId = Date.now();
    const upscaleItem = {
      id: upscaleId,
      resourceContext: { projectId, episodeId },
      status: 'processing',
      imageUrl: null,
      prompt: `高清化: ${img.prompt || ''}`,
      model: '超清',
      aspectRatio: img.aspectRatio,
      createdAt: new Date().toISOString(),
      generationParams: { source: 'super-resolution' },
    };
    setImageList(prev => [upscaleItem, ...prev]);
    toast.info('高清化任务已提交');

    try {
      const submitRes = await authenticatedFetch(`${API_HOST}/api/super-resolution/submit`, {
        method: 'POST',
        body: JSON.stringify({
          imageUrl: img.imageUrl,
          resolution: '4k',
          scale: 50,
          projectId: projectId ? Number(projectId) : null,
          episodeId: episodeId ? Number(episodeId) : null,
          source: 'image_agent_super_resolution',
          resourceTypeCode: 'agent_image',
        }),
      });

      if (!submitRes?.success || !submitRes?.taskId) {
        throw new Error(submitRes?.message || '提交失败');
      }

      const taskId = submitRes.taskId;

      // 轮询结果
      const poll = async () => {
        try {
          const res = await authenticatedFetch(`${API_HOST}/api/super-resolution/status/${taskId}`);
          if (res?.status === 'completed' && res?.resultUrl) {
            setImageList(prev => prev.map(i =>
              i.id === upscaleId ? { ...i, status: 'success', imageUrl: res.resultUrl } : i
            ));
            toast.success('高清化完成');
          } else if (res?.status === 'failed') {
            setImageList(prev => prev.map(i =>
              i.id === upscaleId ? { ...i, status: 'failed', errorMessage: res?.message || '高清化失败' } : i
            ));
            toast.error('高清化失败');
          } else {
            setTimeout(poll, 3000);
          }
        } catch {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      setImageList(prev => prev.map(i =>
        i.id === upscaleId ? { ...i, status: 'failed', errorMessage: err.message } : i
      ));
      toast.error('高清化失败: ' + err.message);
    }
  }, [toast, projectId, episodeId]);

  // 存储轮询 stop 函数
  const pollStopsRef = useRef({});

  // 组件卸载时清理所有轮询
  useEffect(() => {
    return () => {
      Object.values(pollStopsRef.current).forEach(stop => {
        if (typeof stop === 'function') stop();
      });
      pollStopsRef.current = {};
    };
  }, []);

  const handleGenerate = async () => {
    const markedPrompt = getPromptWithMarkers();
    const plainPrompt = stripRefMarkers(markedPrompt);
    const activeReferenceImages = generationMode === 'reference'
      ? images.refs.filter(ref => ref?.url)
      : [];
    if (!plainPrompt.trim() && generationMode !== 'img2img') {
      setError('请输入提示词');
      return;
    }
    if (generationMode === 'img2img' && !images.source && images.refs.length === 0) {
      setError('请上传源图');
      return;
    }
    if (generationMode === 'reference' && activeReferenceImages.length === 0) {
      setError('请上传参考图');
      return;
    }
    if (generationMode === 'reference') {
      try {
        assertWanxImageInputLimit(activeReferenceImages, modelConfig.provider);
      } catch (limitError) {
        setError(limitError.message);
        return;
      }
      if (activeReferenceImages.length > maxReferenceImages) {
        setError(`最多支持${maxReferenceImages}张参考图，请删除多余图片`);
        return;
      }
    }
    if (generationMode === 'region' && !regionData) {
      setError('请先圈选区域');
      return;
    }

    setError(null);

    // 固定本次提交快照；输入草稿保留，后续切换/编辑不会影响已提交任务。
    const currentPrompt = plainPrompt;
    const currentModelConfig = modelConfig;
    const currentConfig = {
      ...config,
      quality: normalizeImageQuality(currentModelConfig, config.quality, generationMode),
    };
    const currentGenerationMode = generationMode;
    const currentRegionData = regionData;
    const currentImages = { ...images, refs: activeReferenceImages };
    const currentImageQualityTier = normalizeWanxImageQualityTier(
      imageQualityTier || DEFAULT_IMAGE_QUALITY_TIER,
      currentModelConfig.provider,
    );

    // 构建生成参数
    const generationParams = {
      modelId: currentModelConfig.id,
      generationMode: currentGenerationMode,
      aspectRatio: currentConfig.aspectRatio,
      imageQualityTier: currentImageQualityTier,
      ...(currentConfig.quality ? { quality: currentConfig.quality } : {}),
      imageModel: currentModelConfig.imageModelByQuality?.[currentImageQualityTier]
        || currentModelConfig.imageModel
        || currentModelConfig.id,
      prompt: currentPrompt,
      markedPrompt,
      referenceImages: currentImages.refs.filter(r => r.url).map(r => r.url),
      ...(currentGenerationMode === 'region' && currentRegionData && {
        regionOriginal: currentRegionData.original,
        regionMask: currentRegionData.mask,
      }),
    };

    // 创建占位 item 并立即插入列表
    const placeholderId = Date.now();
    const placeholderImage = {
      id: placeholderId,
      resourceContext: { projectId, episodeId },
      status: 'processing',
      imageUrl: null,
      prompt: currentPrompt,
      model: currentModelConfig.name,
      modelId: currentModelConfig.id,
      aspectRatio: currentConfig.aspectRatio,
      referenceImages: currentImages.refs.filter(r => r.url).map(r => r.url),
      generationParams,
      createdAt: new Date().toISOString(),
    };
    setImageList(prev => [placeholderImage, ...prev]);

    try {
      let taskType;
      let taskPayload;

      if (currentGenerationMode === 'region') {
        taskType = 'REDRAW_IMAGE';
        taskPayload = {
          provider: currentModelConfig.provider,
          imageModel: currentModelConfig.imageModelByQuality?.[currentImageQualityTier]
            || currentModelConfig.imageModel
            || currentModelConfig.id,
          baseImage: currentRegionData.original,
          maskImage: currentRegionData.mask,
          ...(isDatastartImageProvider(currentModelConfig.provider) ? { aspectRatio: currentConfig.aspectRatio } : {}),
          prompt: currentPrompt,
          imageQualityTier: currentImageQualityTier,
          ...(currentConfig.quality ? { quality: currentConfig.quality } : {}),
          projectId: projectId ? parseInt(projectId) : null,
          episodeId: episodeId ? parseInt(episodeId) : null,
        };
      } else {
        taskType = currentModelConfig.id === 'claude_opus_mj' ? 'SIMPLE_GENERATE_MULTIPLE' : 'SIMPLE_GENERATE';
        taskPayload = {
          ...(currentModelConfig.id === 'claude_opus_mj' ? { count: 1 } : {}),
          provider: currentModelConfig.provider,
          imageModel: currentModelConfig.imageModelByQuality?.[currentImageQualityTier]
            || currentModelConfig.imageModel
            || currentModelConfig.id,
          prompt: currentPrompt,
          aspectRatio: currentConfig.aspectRatio,
          imageQualityTier: currentImageQualityTier,
          ...(currentConfig.quality ? { quality: currentConfig.quality } : {}),
          projectId: projectId ? parseInt(projectId) : null,
          episodeId: episodeId ? parseInt(episodeId) : null,
        };
        if (currentGenerationMode === 'reference' && currentImages.refs.length > 0) {
          taskPayload.referenceImages = currentImages.refs
            .filter(r => r.url)
            .map((r, i) => ({
              name: `参考${i + 1}`,
              base64: r.url
            }));
        }
      }

      const { taskId } = await submitTask(taskType, taskPayload, {
        provider: currentModelConfig.provider,
        projectId: projectId ? parseInt(projectId) : undefined,
        episodeId: episodeId ? parseInt(episodeId) : undefined,
        source: 'image_agent',
      });

      // 开始轮询
      const stop = startTaskPolling(taskId, {
        onSuccess: (result, metadata) => {
          delete pollStopsRef.current[placeholderId];
          const imageUrls = extractTaskResultUrls([metadata?.rawResult, result]);

          if (imageUrls.length === 0) {
            const errorMessage = '任务完成但没有返回图片地址';
            setImageList(prev => prev.map(img =>
              img.id === placeholderId
                ? { ...img, status: 'failed', errorMessage }
                : img
            ));
            toast.error(errorMessage);
            return;
          }

          setImageList(prev => prev.flatMap(img =>
            img.id === placeholderId
              ? imageUrls.map((imageUrl, index) => ({
                ...img,
                id: index === 0 ? placeholderId : `${placeholderId}-${index}`,
                status: 'success',
                imageUrl,
              }))
              : img
          ));

          // 后端 executeSimpleGenerate 已直接保存到 unified_resource，无需前端再调用 saveImageResource

          // MJ 四图的安全引用与回填参数由后端随资源保存，避免旧 base64 覆盖持久 URL。
          // 其余旧单图路径继续非阻塞地补全 metadata。
          if (projectId && generationParams.modelId !== 'claude_opus_mj') {
            imageUrls.forEach(imageUrl => authenticatedFetch(`${API_HOST}/api/resource/metadata`, {
              method: 'PATCH',
              body: JSON.stringify({
                projectId: parseInt(projectId),
                videoUrl: imageUrl,
                metadata: JSON.stringify(generationParams),
              }),
            }).catch(e => console.warn('[ImageAgent] PATCH metadata 失败:', e.message)));
          }

          toast.success(imageUrls.length > 1 ? `图片生成完成，共 ${imageUrls.length} 张！` : '图片生成完成！');
        },
        onError: (errorMessage) => {
          delete pollStopsRef.current[placeholderId];
          setImageList(prev => prev.map(img =>
            img.id === placeholderId
              ? { ...img, status: 'failed', errorMessage }
              : img
          ));
          toast.error(`生成失败: ${errorMessage}`);
        },
      });

      pollStopsRef.current[placeholderId] = stop;

    } catch (err) {
      console.error('提交失败:', err);
      setImageList(prev => prev.map(img =>
        img.id === placeholderId
          ? { ...img, status: 'failed', errorMessage: err.message }
          : img
      ));
      setError(`提交失败: ${err.message}`);
      toast.error(`提交失败: ${err.message}`);
    }
  };

  return (
    <div className="studio-generation h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* editor 样式 */}
      <style>{`
        .ia-editor[data-placeholder]:empty:before { content: attr(data-placeholder); color: var(--color-text-muted); pointer-events: none; }
        .ia-editor:focus { outline: none; }
        .ia-editor [data-mention] { user-select: all; }
        .ia-card { border-radius: var(--workspace-radius-md); box-shadow: none; }
        .ia-dot { width: 6px; height: 6px; border-radius: 9999px; display: inline-block; flex-shrink: 0; }
      `}</style>
      {/* 圈选编辑器弹窗 */}
      {showRegionEditor && images.source && (
        <RegionSelector
          imageUrl={images.source}
          onRegionSelected={handleRegionSelected}
          onCancel={() => setShowRegionEditor(false)}
        />
      )}

      {/* 顶部标题栏 */}
      <div className="generation-header shrink-0 px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-primary)' }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
            <Palette size={18} style={{ color: 'var(--color-text-secondary)' }} />
          </div>
          <div>
            <h1 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>生图站</h1>
            <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              {imageList.length} 张图片
            </p>
          </div>
        </div>
      </div>

      {/* 主内容区：历史图片流 */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4">
        {imageList.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <EmptyState
              icon={ImageIcon}
              title="暂无图片"
              description="在下方输入提示词开始生成图片。支持文生图、参考图生图和区域编辑三种模式。"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {imageList.map(image => (
              <ImageCard
                key={image.id}
                image={image}
                onDelete={handleDeleteImage}
                onRetry={handleRetry}
                onUpscale={handleUpscale}
                onHoverPreview={setHoverPreview}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── 输入框卡片 ── */}
      <div className="generation-input-dock shrink-0 px-5 pb-4 pt-2" style={{ backgroundColor: 'var(--color-bg-primary)', borderTop: '1px solid var(--color-border-secondary)' }}>
        {/* 错误提示 */}
        {error && (
          <div className="mb-2 p-2 rounded-lg flex items-center gap-2 text-xs" style={{ backgroundColor: 'var(--color-red-light)', color: 'var(--color-error)' }}>
            <AlertCircle size={14} /> {error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={12} /></button>
          </div>
        )}

        {/* 参考素材预览条 — 在输入框上方 */}
        {(generationMode === 'reference' || generationMode === 'region') && (
          <div className="mb-2 flex items-center gap-2 flex-wrap">
            {/* 参考生图模式 */}
            {generationMode === 'reference' && (
              <>
                {images.refs.map((ref, i) => (
                  <div key={ref.id} className="relative group w-14 h-14 rounded-md shrink-0"
                    style={{ border: '1px solid var(--color-border-primary)' }}
                    onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setHoverPreview({ url: ref.url, x: r.left + r.width / 2, y: r.top }); }}
                    onMouseLeave={() => setHoverPreview(null)}
                  >
                    <img src={ref.url} alt="" className="w-full h-full object-cover rounded-md" />
                    <div className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>图片{i + 1}</div>
                    <button onClick={() => removeImage('refs', ref.id)} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove generation-feedback z-10" style={{ backgroundColor: 'var(--color-error)', color: 'white' }}><X size={8} /></button>
                  </div>
                ))}
                {/* 添加按钮 — 弹窗菜单 */}
                <div className="relative shrink-0" ref={addMenuRef}>
                  <button
                    onClick={() => setShowAddMenu(!showAddMenu)}
                    disabled={images.refs.length >= maxReferenceImages}
                    className="ia-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 generation-feedback"
                    style={{ border: `1px dashed ${modelConfig.color}50`, color: modelConfig.color }}
                    title={images.refs.length >= maxReferenceImages ? `最多${maxReferenceImages}张参考图` : '添加参考图'}
                  >
                    <Plus size={16} />
                    <span className="text-2xs font-medium">{images.refs.length >= maxReferenceImages ? '已满' : '添加'}</span>
                  </button>
                  {showAddMenu && ReactDOM.createPortal(
                    <div className="fixed inset-0 z-[300]" onMouseDown={() => setShowAddMenu(false)}>
                      <div
                        className="absolute w-52 rounded-lg shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-150"
                        style={{
                          bottom: window.innerHeight - (addMenuRef.current?.getBoundingClientRect().top || 0) + 8,
                          left: addMenuRef.current?.getBoundingClientRect().left || 16,
                          backgroundColor: 'var(--color-bg-secondary)',
                          border: '1px solid var(--color-border-primary)',
                        }}
                        onClick={e => e.stopPropagation()}
                        onMouseDown={e => e.stopPropagation()}
                      >
                        <label onClick={e => e.stopPropagation()} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs cursor-pointer transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}>
                          <ImageIcon size={14} style={{ color: modelConfig.color }} /> 上传图片
                          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={e => { handleImageUpload('refs', e.target.files[0]); setShowAddMenu(false); }} />
                        </label>
                        <button onClick={(e) => { e.stopPropagation(); setShowResourceSelector(true); setShowAddMenu(false); }} className="w-full flex items-center gap-2.5 px-4 py-3 text-xs transition-colors hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-primary)' }}><FolderOpen size={14} style={{ color: modelConfig.color }} /> 从资源库选择</button>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              </>
            )}
            {/* 圈选处理模式 */}
            {generationMode === 'region' && (
              <>
                {!images.source ? (
                  <label className="ia-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 cursor-pointer generation-feedback" style={{ border: `1px dashed ${modelConfig.color}50`, color: modelConfig.color }}>
                    <Upload size={16} />
                    <span className="text-2xs font-medium">上传原图</span>
                    <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload('source', e.target.files[0])} />
                  </label>
                ) : (
                  <div className="relative group w-14 h-14 rounded-md shrink-0" style={{ border: regionData ? '2px solid var(--color-success)' : '1px solid var(--color-border-primary)' }}>
                    <img src={images.source} alt="" className="w-full h-full object-cover rounded-md" />
                    <div className="absolute bottom-0 left-0 right-0 text-center text-2xs font-bold py-px rounded-b-md" style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}>{regionData ? '已圈选' : '原图'}</div>
                    <button onClick={() => removeImage('source')} className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center generation-reference-remove generation-feedback z-10" style={{ backgroundColor: 'var(--color-error)', color: 'white' }}><X size={8} /></button>
                  </div>
                )}
                {images.source && (
                  <button onClick={() => setShowRegionEditor(true)} className="ia-btn w-14 h-14 rounded-md flex flex-col items-center justify-center gap-1 shrink-0 generation-feedback" style={{ border: `1px dashed ${modelConfig.color}50`, color: modelConfig.color }}>
                    <Scissors size={16} />
                    <span className="text-2xs font-medium">{regionData ? '重新圈选' : '圈选'}</span>
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {generationMode !== 'reference' && images.refs.length > 0 && (
          <div className="mb-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
            已保留 {images.refs.length} 张参考图，切回参考生图可继续使用。
          </div>
        )}
        {generationMode !== 'region' && images.source && (
          <div className="mb-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
            圈选原图{regionData ? '和蒙版' : ''}已保留，切回支持圈选的模型和模式可继续编辑。
          </div>
        )}

        {/* 输入框卡片 */}
        <div className="generation-composer production-panel" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
          {/* 上半部分：contentEditable 输入框 */}
          <div className="flex items-start gap-2 p-3">
            <div className="relative flex-1">
              <div
                ref={editorRef}
                contentEditable
                role="textbox"
                aria-label="图片提示词"
                aria-multiline="true"
                suppressContentEditableWarning
                onInput={handleEditorInput}
                onKeyDown={handleEditorKeyDown}
                onPaste={(e) => { const html = e.clipboardData.getData('text/html'); if (html && html.includes('data-mention')) return; e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); }}
                data-placeholder={generationMode === 'region' ? '描述如何处理圈选区域...输入 @ 引用参考图' : '描述你想要的图片...输入 @ 引用参考图'}
                className="ia-editor flex-1 bg-transparent text-sm outline-none leading-relaxed py-1 min-h-[128px] max-h-[280px] overflow-y-auto whitespace-pre-wrap break-words"
                style={{ color: 'var(--color-text-primary)' }}
              />
              {/* @ 提及弹窗 */}
              {mentionPopover && (filteredMentions.length > 0 || menuLoading) && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[300]" onMouseDown={() => setMentionPopover(null)}>
                  <div
                    ref={mentionListRef}
                    className="studio-generation generation-popover fixed w-64 rounded-lg py-2 px-1 max-h-72 overflow-y-auto custom-scrollbar"
                    style={{
                      ...((() => {
                        const sel = window.getSelection();
                        let cursorRect = null;
                        if (sel?.rangeCount > 0) {
                          const range = sel.getRangeAt(0);
                          const rects = range.getClientRects();
                          cursorRect = rects.length > 0 ? rects[0] : range.getBoundingClientRect();
                        }
                        const fallback = editorRef.current?.getBoundingClientRect();
                        const bottom = cursorRect?.bottom || fallback?.bottom || 0;
                        const top = cursorRect?.top || fallback?.top || 0;
                        let left = cursorRect?.left || fallback?.left || 0;
                        const menuW = 260;
                        if (left + menuW > window.innerWidth - 8) left = window.innerWidth - menuW - 8;
                        if (left < 8) left = 8;
                        if (top < 300) {
                          return { top: bottom + 6, left };
                        }
                        return { bottom: window.innerHeight - top + 6, left };
                      })()),
                      backgroundColor: 'var(--color-bg-secondary)',
                      border: '1px solid var(--color-border-primary)',
                    }}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    {/* 面包屑导航 */}
                    {mentionPopover.path?.length > 0 && (
                      <div className="flex items-center gap-1 px-3 py-1.5 mb-1" style={{ borderBottom: '1px solid var(--color-border-secondary)' }}>
                        <button onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleMenuBack(); }} className="text-2xs hover:underline" style={{ color: 'var(--color-text-muted)' }}>←</button>
                        {mentionPopover.path.map((p, i) => (
                          <span key={i} className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{i > 0 && ' › '}{p}</span>
                        ))}
                      </div>
                    )}
                    {menuLoading && (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 size={16} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />
                      </div>
                    )}
                    {!menuLoading && filteredMentions.map((item, i) => (
                      <button
                        key={item.id}
                        ref={el => mentionItemRefs.current[i] = el}
                        data-image-url={item.imageUrl || ''}
                        onMouseDown={(e) => {
                          e.preventDefault(); e.stopPropagation();
                          setMentionPreviewUrl(null);
                          if (item.action === 'select') handleMenuSelect(item);
                          else handleMenuNavigate(item);
                        }}
                        onMouseEnter={() => updateMentionIndex(i)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-left transition-colors hover:bg-[var(--color-bg-tertiary)]"
                        style={{
                          color: i === 0 && mentionIndexRef.current === 0 ? (modelConfig.color || 'var(--color-text-primary)') : 'var(--color-text-secondary)',
                          backgroundColor: i === 0 && mentionIndexRef.current === 0 ? `${modelConfig.color || '#3B82F6'}18` : 'transparent',
                        }}
                      >
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" style={{ border: '1px solid var(--color-border-primary)' }} />
                        ) : item.icon ? (
                          <span className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }}><item.icon size={16} /></span>
                        ) : null}
                        <span className="truncate flex-1">{item.label || item.name}</span>
                        {item.action === 'navigate' && <span style={{ color: 'var(--color-text-muted)' }}>›</span>}
                      </button>
                    ))}
                    {!menuLoading && filteredMentions.length === 0 && (
                      <div className="px-3 py-4 text-center text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无内容</div>
                    )}
                  </div>
                </div>,
                document.body
              )}
              {/* @ 本地上传隐藏 input */}
              <input type="file" ref={mentionUploadRef} className="hidden" accept="image/*" onChange={handleMentionUpload} />
            </div>
          </div>

          {/* 下半部分：模型 + 模式 + 比例 + 发送 */}
          <div className="generation-controls flex items-center justify-between px-3 pb-2.5 pt-0 gap-2">
            <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
              {/* 大类是入口，下拉项仍是具体型号和独立代理线路。 */}
              {IMAGE_MODEL_FAMILIES.map(family => {
                const models = family.modelIds.map(id => IMAGE_MODEL_CONFIGS[id]).filter(Boolean);
                const isSelected = family.modelIds.includes(selectedModel);
                return (
                  <AppSelect
                    key={family.id}
                    data-model-family={family.id}
                    data-active={isSelected}
                    title={isSelected ? `当前：${modelConfig.name}` : `选择 ${family.label} 型号和线路`}
                    aria-label={`${family.label} 型号和线路`}
                    displayValue={family.label}
                    value={isSelected ? selectedModel : ''}
                    onChange={event => selectImageModel(event.target.value)}
                    className="relative inline-flex items-center gap-1.5 shrink-0 px-2.5 py-1.5 rounded-md text-xs font-medium focus-within:outline focus-within:outline-2 focus-within:outline-[var(--color-accent-primary)]"
                    style={{
                      backgroundColor: isSelected ? 'var(--color-accent-light)' : 'var(--color-bg-tertiary)',
                      color: isSelected ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)',
                      border: `1px solid ${isSelected ? 'var(--color-accent-primary)' : 'var(--color-border-primary)'}`,
                      boxShadow: 'none',
                    }}
                  >
                    <option value="" disabled>选择 {family.label} 型号和线路</option>
                    {models.map(model => {
                      const recommendation = getGlobalProviderRecommendation(model.id === 'gptimage' ? 'huobao_gpt' : model.id);
                      return <option key={model.id} value={model.id} title={recommendation || model.name}>{model.name}</option>;
                    })}
                  </AppSelect>
                );
              })}
              <span className="max-w-[260px] truncate text-2xs" style={{ color: 'var(--color-text-muted)' }} title={`当前模型：${modelConfig.name}`}>
                当前：{modelConfig.name}
              </span>
              {isDatastartImageProvider(modelConfig.provider) && (
                <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }} title={DATASTART_IMAGE_CAPABILITY_HINT}>
                  当前接入：单张参考图
                </span>
              )}

              <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

              {/* 生成模式 */}
              {[
                { id: 'text2img', label: '文生图', icon: Sparkles },
                { id: 'reference', label: '参考生图', icon: Layers },
                { id: 'region', label: '圈选处理', icon: Scissors },
              ].filter(m => modelConfig?.modes?.includes(m.id)).map(mode => (
                <button
                  key={mode.id}
                  onClick={() => { setGenerationMode(mode.id); setShowRegionEditor(false); }}
                  className="ia-btn generation-choice flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium whitespace-nowrap"
                  aria-pressed={generationMode === mode.id}
                  style={{
                    backgroundColor: generationMode === mode.id ? 'var(--color-accent-light)' : 'transparent',
                    color: generationMode === mode.id ? 'var(--color-accent-primary)' : 'var(--color-text-muted)',
                    border: `1px solid ${generationMode === mode.id ? 'var(--color-accent-primary)' : 'transparent'}`,
                  }}
                >
                  <mode.icon size={10} />
                  {mode.label}
                </button>
              ))}

              <div className="w-px h-4 mx-0.5" style={{ backgroundColor: 'var(--color-border-primary)' }} />

              {/* 比例选择 */}
              <AppSelect
                value={config.aspectRatio || ''}
                aria-label="图片比例"
                onChange={e => updateConfig('aspectRatio', e.target.value)}
                className="px-2 py-1 rounded-md text-2xs font-medium outline-none"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}
              >
                {modelConfig?.aspectRatioOptions?.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </AppSelect>

              {/* 清晰度档位 */}
              <AppSelect
                value={normalizeWanxImageQualityTier(imageQualityTier || DEFAULT_IMAGE_QUALITY_TIER, modelConfig.provider)}
                aria-label="图片清晰度"
                onChange={e => handleImageQualityTierChange(e.target.value)}
                className="px-2 py-1 rounded-md text-2xs font-medium outline-none"
                title="图片清晰度档位"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}
              >
                {availableQualityTierOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </AppSelect>

              {modelConfig?.qualityOptions?.length > 0 && (
                <AppSelect
                  value={normalizeImageQuality(modelConfig, config.quality, generationMode)}
                  aria-label="图片质量"
                  onChange={e => updateConfig('quality', e.target.value)}
                  className="px-2 py-1 rounded-md text-2xs font-medium outline-none"
                  title="图片生成质量"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}
                >
                  {getImageQualityOptions(modelConfig, generationMode).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </AppSelect>
              )}
            </div>

            {/* 发送按钮 */}
            <button
              onClick={handleGenerate}
              className="ia-btn generation-primary-action p-2.5 rounded-md text-[var(--color-accent-contrast)] shrink-0"
              style={{ backgroundColor: theme.colors.accentPrimary, boxShadow: 'none' }}
              aria-label="生成图片"
              title="生成图片"
            >
              <Wand2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* 资源选择器 */}
      <ResourceSelector
        isOpen={showResourceSelector}
        onClose={() => setShowResourceSelector(false)}
        onSelect={(resource) => {
          if (resource && resource.url) {
            if (images.refs.length >= maxReferenceImages) {
              toast.warning(`${modelConfig.name}最多支持${maxReferenceImages}张参考图`);
              setShowResourceSelector(false);
              return;
            }
            setImages(prev => {
              const newRefs = [...prev.refs, { id: Date.now(), url: resource.url, name: resource.name }];
              appendChipToEditor(resource.name || `图片${newRefs.length}`, resource.url);
              return { ...prev, refs: newRefs };
            });
          }
          setShowResourceSelector(false);
        }}
        projectId={projectId}
        type="image"
        title="选择参考图"
      />

      {/* 删除确认弹窗 */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteImage}
        title={deleteTarget?.status === 'success' ? '移入回收站' : '移除本地记录'}
        message={deleteTarget?.status === 'success' ? '确定将这张图片移入回收站吗？移入后将不再显示，可联系管理员恢复。' : '仅移除本地任务卡片，不会取消已提交的生成任务。'}
        confirmLabel={deleteTarget?.status === 'success' ? '移入回收站' : '移除记录'}
        variant="danger"
      />

      {/* 参考图 hover 预览 */}
      {hoverPreview && ReactDOM.createPortal(
        <div className="fixed z-[400] pointer-events-none" style={{
          left: Math.max(8, Math.min(hoverPreview.x - 160, window.innerWidth - 328)),
          bottom: window.innerHeight - hoverPreview.y + 8,
        }}>
          <img src={hoverPreview.url} alt="" className="rounded-xl shadow-2xl animate-in fade-in zoom-in-95 duration-150" style={{
            maxWidth: 'min(320px, 40vw)', maxHeight: 'min(400px, 50vh)', width: 'auto', height: 'auto', objectFit: 'contain',
            backgroundColor: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border-primary)',
            padding: '4px',
          }} />
        </div>,
        document.body
      )}
    </div>
  );
};

export default ImageAgentModern;
